import { z } from "zod";

// =============================================================================
// ACTION MODIFIERS
// =============================================================================

export const deleteModifierValueSchema = z
  .object({
    type: z.literal("delete"),
    value: z.any(),
  })
  .describe("DeleteModifierValue");

// =============================================================================
// PRODUCT MODIFIERS
// =============================================================================

export const productModifierValueSchema = z
  .object({
    type: z.literal("product"),
    value: z.string(),
  })
  .describe("ProductModifierValue");

export const variantModifierValueSchema = z
  .object({
    type: z.literal("variant"),
    value: z.string(),
  })
  .describe("VariantModifierValue");

// =============================================================================
// TEXT & TYPOGRAPHY MODIFIERS
// =============================================================================

export const textContentModifierValueSchema = z
  .object({
    type: z.literal("content"),
    value: z.string(),
    className: z.string().optional(),
  })
  .describe("TextContentModifierValue");

export const textAlignmentModifierValueSchema = z
  .object({
    type: z.literal("alignment"),
    value: z.enum(["left", "center", "right", "justify"]),
  })
  .describe("TextAlignmentModifierValue");

export const textFontWeightModifierValueSchema = z
  .object({
    type: z.literal("fontWeight"),
    value: z.number(),
  })
  .describe("TextFontWeightModifierValue");

export const textFontSizeModifierValueSchema = z
  .object({
    type: z.literal("fontSize"),
    value: z.number(),
  })
  .describe("TextFontSizeModifierValue");

export const textLineHeightModifierValueSchema = z
  .object({
    type: z.literal("lineHeight"),
    value: z.number(),
  })
  .describe("TextLineHeightModifierValue");

export const textSpacingModifierValueSchema = z
  .object({
    type: z.literal("textSpacing"),
    value: z.number(),
  })
  .describe("TextSpacingModifierValue");

export const fontFamilyModifierValueSchema = z
  .object({
    type: z.literal("fontFamily"),
    value: z.string(),
  })
  .describe("FontFamilyModifierValue");

export const textShadowModifierValueSchema = z
  .object({
    type: z.literal("textShadow"),
    value: z.object({
      offsetX: z.number(),
      offsetY: z.number(),
      blurRadius: z.number().optional(),
      color: z.string().optional(),
    }),
  })
  .describe("TextShadowModifierValue");

export const textTagModifierValueSchema = z
  .object({
    type: z.literal("textTag"),
    value: z.enum(["h1", "h2", "h3", "h4", "p"]),
  })
  .describe("TextTagModifierValue");

// =============================================================================
// LAYOUT & SPACING MODIFIERS
// =============================================================================

export const paddingModifierValueSchema = z
  .object({
    type: z.literal("padding"),
    direction: z.enum(["top", "right", "bottom", "left"]),
    value: z.number(),
  })
  .describe("PaddingModifierValue");

export const marginModifierValueSchema = z
  .object({
    type: z.literal("margin"),
    direction: z.enum(["top", "right", "bottom", "left"]),
    value: z.number(),
  })
  .describe("MarginModifierValue");

export const dimensionModifierValueSchema = z
  .object({
    type: z.literal("dimension"),
    side: z.enum(["width", "height"]),
    value: z.union([z.number(), z.enum(["auto", "full"])]),
  })
  .describe("DimensionModifierValue");

export const boxShadowModifierValueSchema = z
  .object({
    type: z.literal("boxShadow"),
    value: z.enum(["none", "2xs", "xs", "sm", "md", "lg", "xl", "2xl"]),
  })
  .describe("BoxShadowModifierValue");

export const directionModifierValueSchema = z
  .object({
    type: z.literal("containerDirection"),
    value: z.enum(["row", "column"]),
  })
  .describe("DirectionModifierValue");

export const containerAlignmentModifierValueSchema = z
  .object({
    type: z.literal("containerAlignment"),
    value: z.enum(["start", "center", "end"]),
  })
  .describe("ContainerAlignmentModifierValue");

export const gapModifierValueSchema = z
  .object({
    type: z.literal("containerGap"),
    value: z.number(),
  })
  .describe("GapModifierValue");

// =============================================================================
// APPEARANCE & VISUAL MODIFIERS
// =============================================================================

export const opacityModifierValueSchema = z
  .object({
    type: z.literal("opacity"),
    value: z.number(),
  })
  .describe("OpacityModifierValue");

export const colorModifierValueSchema = z
  .object({
    type: z.literal("color"),
    value: z.string().nullable(),
  })
  .describe("ColorModifierValue");

export const backgroundColorModifierValueSchema = z
  .object({
    type: z.literal("backgroundColor"),
    value: z.string().nullable(),
  })
  .describe("BackgroundColorModifierValue");

export const borderColorModifierValueSchema = z
  .object({
    type: z.literal("borderColor"),
    value: z.string().nullable(),
  })
  .describe("BorderColorModifierValue");

export const borderStyleModifierValueSchema = z
  .object({
    type: z.literal("borderStyle"),
    value: z.enum(["none", "solid", "dashed", "dotted", "double"]),
  })
  .describe("BorderStyleModifierValue");

export const borderWidthModifierValueSchema = z
  .object({
    type: z.literal("borderWidth"),
    direction: z.enum(["top", "right", "bottom", "left"]),
    value: z.number(),
  })
  .describe("BorderWidthModifierValue");

export const cornerRadiusModifierValueSchema = z
  .object({
    type: z.literal("cornerRadius"),
    corner: z.enum(["topLeft", "topRight", "bottomRight", "bottomLeft"]),
    radius: z.number(),
  })
  .describe("CornerRadiusModifierValue");

// =============================================================================
// ASSET MODIFIERS
// =============================================================================

export const assetModifierValueSchema = z
  .object({
    type: z.literal("asset"),
    source: z.string(),
  })
  .describe("AssetModifierValue");

// =============================================================================
// LINK MODIFIERS
// =============================================================================

export const hrefModifierValueSchema = z
  .object({
    type: z.literal("href"),
    value: z.string(),
  })
  .describe("HrefModifierValue");

// =============================================================================
// ELEMENT-SPECIFIC MODIFIER UNIONS
// =============================================================================

export const textBlockModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    textContentModifierValueSchema,
    textAlignmentModifierValueSchema,
    colorModifierValueSchema,
    paddingModifierValueSchema,
    textShadowModifierValueSchema,
    textFontWeightModifierValueSchema,
    textFontSizeModifierValueSchema,
    textLineHeightModifierValueSchema,
    textSpacingModifierValueSchema,
    fontFamilyModifierValueSchema,
    textTagModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("TextBlockModifierValue");

export const imageModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    dimensionModifierValueSchema,
    marginModifierValueSchema,
    boxShadowModifierValueSchema,
    assetModifierValueSchema,
    borderColorModifierValueSchema,
    borderStyleModifierValueSchema,
    borderWidthModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("ImageModifierValue");

export const containerModifierValueSchema = z
  .discriminatedUnion("type", [
    directionModifierValueSchema,
    containerAlignmentModifierValueSchema,
    gapModifierValueSchema,
    paddingModifierValueSchema,
    marginModifierValueSchema,
    backgroundColorModifierValueSchema,
    borderColorModifierValueSchema,
    borderStyleModifierValueSchema,
    borderWidthModifierValueSchema,
    cornerRadiusModifierValueSchema,
    opacityModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("ContainerModifierValue");

export const buttonModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    textContentModifierValueSchema,
    textAlignmentModifierValueSchema,
    colorModifierValueSchema,
    paddingModifierValueSchema,
    textShadowModifierValueSchema,
    textFontWeightModifierValueSchema,
    textFontSizeModifierValueSchema,
    textLineHeightModifierValueSchema,
    textSpacingModifierValueSchema,
    fontFamilyModifierValueSchema,
    textTagModifierValueSchema,
    boxShadowModifierValueSchema,
    backgroundColorModifierValueSchema,
    borderColorModifierValueSchema,
    borderStyleModifierValueSchema,
    borderWidthModifierValueSchema,
    cornerRadiusModifierValueSchema,
    opacityModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("ButtonModifierValue");

export const iconModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    colorModifierValueSchema,
    paddingModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("IconModifierValue");

export const universalModifierValueSchema = z
  .union([
    textBlockModifierValueSchema,
    imageModifierValueSchema,
    containerModifierValueSchema,
    buttonModifierValueSchema,
    iconModifierValueSchema,
    hrefModifierValueSchema,
  ])
  .describe("UniversalModifierValue");

export const elementSelectorSchema = z
  .object({
    byteOffset: z.number(),
  })
  .describe("ElementSelector");

export const selectedElementDynamicDataSourcePayloadSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("product"),
      productId: z.string(),
      variantId: z.string().optional(),
    }),
  ])
  .describe("SelectedElementDynamicDataSource");

export const modifiableElementTypeSchema = z
  .enum([
    "text",
    "container",
    "image",
    "video",
    "product-section",
    "button",
    "unknown",
    "link",
    "icon",
  ])
  .describe("ModifiableElementType");

export const selectedElementDesignationElementSchema = z
  .object({
    type: z.literal("default"),
    element: z.object({
      type: modifiableElementTypeSchema,
      byteOffset: z.number(),
      innerHtml: z.string(),
      tagName: z.string(),
      props: z
        .record(z.string(), z.unknown())
        .and(
          z
            .object({
              className: z.string().nullable(),
              src: z.string().nullable(),
            })
            .partial(),
        )
        .nullable(),
      styleOverrides: z
        .record(z.string(), z.unknown())
        .and(
          z
            .object({
              width: z.union([z.number(), z.enum(["auto", "full"])]).nullable(),
              height: z
                .union([z.number(), z.enum(["auto", "full"])])
                .nullable(),
              letterSpacing: z.number().nullable(),
              fontSize: z.number().nullable(),
              lineHeight: z
                .union([z.number(), z.literal(["normal"])])
                .nullable(),
              colorHex: z.string().nullable(),
              backgroundColorHex: z.string().nullable(),
              borderColorHex: z.string().nullable(),
              marginTop: z.number().nullable(),
              marginRight: z.number().nullable(),
              marginBottom: z.number().nullable(),
              marginLeft: z.number().nullable(),
              paddingTop: z.number().nullable(),
              paddingRight: z.number().nullable(),
              paddingBottom: z.number().nullable(),
              paddingLeft: z.number().nullable(),
              fontWeight: z.number().nullable(),
              fontStyle: z.string().nullable(),
              textDecorationLine: z.string().nullable(),
              textAlign: z.string().nullable(),
              opacity: z.number().nullable(),
              gap: z.number().nullable(),
              fontFamily: z.string().nullable(),
              cornerTopLeftRadius: z.number().nullable(),
              cornerTopRightRadius: z.number().nullable(),
              cornerBottomRightRadius: z.number().nullable(),
              cornerBottomLeftRadius: z.number().nullable(),
              borderTopWidth: z.number().nullable(),
              borderRightWidth: z.number().nullable(),
              borderBottomWidth: z.number().nullable(),
              borderLeftWidth: z.number().nullable(),
              borderTopStyle: z.string().nullable(),
              borderRightStyle: z.string().nullable(),
              borderBottomStyle: z.string().nullable(),
              borderLeftStyle: z.string().nullable(),
            })
            .partial(),
        )
        .nullable(),
    }),
  })
  .describe("SelectedElementDesignationElement");

export const selectedElementDesignationSchema = z
  .discriminatedUnion("type", [
    selectedElementDesignationElementSchema,
    z.object({
      type: z.literal("link"),
      href: z.string().nullish(),
      target: z.string().nullish(),
      element: z.object({
        byteOffset: z.number(),
      }),
    }),
    z.object({
      type: z.literal("dynamicDataSource"),
      data: selectedElementDynamicDataSourcePayloadSchema,
      element: z.object({
        byteOffset: z.number(),
      }),
    }),
  ])
  .describe("SelectedElementDesignation");

export const fiberSelectedPayloadSchema = z
  .object({
    selectedElementDesignations: z.array(selectedElementDesignationSchema),
  })
  .describe("FiberSelectedPayload");
